<script lang="ts" setup>
import { computed, ref } from 'vue'
import type { WelfarePageConfig } from '../configs/types'
import WelfareGuideAddScreen from './modules/WelfareGuideAddScreen.vue'
import WelfareGuideOpen from './modules/WelfareGuideOpen.vue'
import { brand, theme as brandTheme } from '@/config/brands'

const props = defineProps<{
  config: WelfarePageConfig
  homeBgUrl: string
  btnBgColor: string
  signed: boolean
  progress: any[]
  modalSigned: boolean
  progressInfo: any
  tomorrowInfo: any
  checkInDayNum: number
  nextCheckInCountdown: string
  signedNoticePopup: boolean
  showTabbar: boolean
  tasks: any[]
  showGuidePopupAddHome: boolean
  showGuideOpen: boolean
  finishPopup: boolean
  coins: number
  completeTaskDescription: string
  userCoinBalance: number
  getBtnStatus: (item: any) => string
  onCheckIn: () => void
  onSignedCheckIn: () => void
  onBrowseGenres: () => void
  handleTask: (item: any) => void
  onCheckInOk: () => void
}>()

const emit = defineEmits<{
  (e: 'update:modal-signed', value: boolean): void
  (e: 'update:signed-notice-popup', value: boolean): void
  (e: 'update:show-guide-popup-add-home', value: boolean): void
  (e: 'update:show-guide-open', value: boolean): void
  (e: 'update:finish-popup', value: boolean): void
}>()

const topMaskBackground = `linear-gradient(180deg, ${brandTheme.bgGlobal || '#121214'} 0%, transparent 100%)`
const isMoonpage = brand === 'moonpage'
const rulesOpen = ref(false)
const moonpageActionGradient = computed(() => props.config.theme.checkinBtnGradient || brandTheme.primaryColor)

const streakSubtitle = computed(() => props.signed
  ? props.config.texts.moonpageStreakActive(props.checkInDayNum)
  : props.config.texts.moonpageStreakStart)

const todayDayNum = computed(() => {
  if (props.signed)
    return props.checkInDayNum
  return props.progressInfo?.num || props.progress.find(item => !item.isSign)?.num || 1
})

const tomorrowReward = computed(() => {
  if (props.tomorrowInfo)
    return displayPrize(props.tomorrowInfo)
  return displayPrize(props.progress[0])
})

function displayPrize(item: any) {
  return item?.prizeNum ?? 0
}

function isToday(item: any) {
  return Number(item?.num) === Number(todayDayNum.value)
}

function isLastDay(item: any, index: number) {
  return Number(item?.num) === 7 || index === props.progress.length - 1
}

function handlePrimaryClick() {
  if (props.signed) {
    props.onSignedCheckIn()
    return
  }
  props.onCheckIn()
}
</script>

<template>
  <div class="min-h-100dvh pb-4 bg-n-bg-global">
    <!-- Cover -->
    <div class="cover w-full relative bg-n-bg-global" :style="{ backgroundImage: homeBgUrl }">
      <!-- 深色渐变遮罩，确保背景图顶部不会是白色 -->
      <div
        class="absolute top-0 left-0 right-0 h-120px z-0 pointer-events-none"
        :style="{ background: topMaskBackground }"
      />
      <div class="rr-moonpage-wallet-row relative z-2 flex justify-between items-center px-5 pt-3.5 pb-3">
        <div class="min-w-0">
          <h1 class="text-12px text-n-text-2 whitespace-nowrap">
            {{ config.texts.myWallet }}
          </h1>
        </div>
        <div class="flex items-center">
          <img class="w-24px h-24px" :src="config.resources.welfareGold">
          <strong class="ml-5px text-16px text-n-text-1">{{ userCoinBalance }}</strong>
          <span class="ml-5px text-12px text-n-text-2">{{ config.texts.coins }}</span>
        </div>
      </div>
      <div class="relative z-2 mx-10px px-10px py-16px bg-n-bg-card b-rd-2 rr-welfare-module">
        <div class="flex items-center justify-between">
          <h3 class="rr-section-title text-18px font-bold text-n-text-1">
            {{ config.texts.dailyCheckIn }}
          </h3>
          <button
            v-if="isMoonpage"
            class="rr-rules-toggle flex items-center justify-center text-12px font-bold"
            type="button"
            :aria-label="config.texts.moonpageShowRules"
            @click="rulesOpen = !rulesOpen"
          >
            ?
          </button>
        </div>
        <p v-if="isMoonpage" class="text-12px text-n-text-3">
          {{ streakSubtitle }}
        </p>
        <p v-else class="text-12px text-n-text-3">
          {{ config.texts.youHaveCheckedIn }} {{ checkInDayNum }} {{ config.texts.dayStraight }}
        </p>
        <div v-if="isMoonpage && rulesOpen" class="rr-rules-panel mt-10px text-11px text-n-text-2">
          <p>
            {{ config.texts.moonpageRuleDaily }}
          </p>
          <p>
            {{ config.texts.moonpageRuleBonus }}
          </p>
          <p>
            {{ config.texts.moonpageRuleReset }}
          </p>
        </div>
        <div class="mt-15px px-4px">
          <!-- Moonpage: completed cards are muted, today is outlined, and Day 7 is a bonus card. -->
          <div v-if="isMoonpage" class="rr-moonpage-day-grid">
            <div
              v-for="(item, index) in progress"
              :key="item.num"
              class="rr-moonpage-day-card text-center"
              :class="{
                'rr-moonpage-day-card--done': item.isSign,
                'rr-moonpage-day-card--today': isToday(item),
                'rr-moonpage-day-card--bonus': isLastDay(item, index),
              }"
            >
              <span v-if="isToday(item)" class="rr-moonpage-today-tag">{{ config.texts.moonpageToday }}</span>
              <span v-if="item.isSign" class="rr-moonpage-check-badge" :aria-label="config.texts.moonpageCompleted">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <path d="m7.5 12.25 3 3 6-6" />
                </svg>
              </span>
              <img v-else-if="isLastDay(item, index)" class="rr-moonpage-gift" :src="config.resources.welfareBox" :alt="config.texts.moonpageBonusReward">
              <img v-else class="rr-moonpage-coin" :src="config.resources.welfareGold" :alt="config.texts.moonpageCoinReward">
              <strong class="rr-moonpage-prize">+{{ displayPrize(item) }}</strong>
              <span class="rr-moonpage-day-label">{{ isLastDay(item, index) ? config.texts.moonpageDay7Bonus : config.texts.day(item.num) }}</span>
            </div>
          </div>

          <!-- Other Flipnovel-family brands keep the existing reward grid. -->
          <template v-else>
            <!-- 第一行 4 天 -->
            <div class="grid grid-cols-4 space-x-8px">
              <div
                v-for="(item, index) in progress.slice(0, 4)"
                :key="item.num"
                class="text-center"
                :class="{ 'opacity-70': item.isSign }"
              >
                <div class="flex flex-col items-center pt-10px pb-6px bg-n-bg-tag b-rd-5px">
                  <img v-if="index !== progress.length - 1" class="w-24px h-24px" :src="config.resources.welfareGold">
                  <img v-else class="w-28px h-25px mb--1px" :src="config.resources.welfareBox">
                  <span class="mt-3px text-10px text-n-text-1">+{{ item.prizeNum }}</span>
                </div>
                <div class="flex justify-center">
                  <i-assets-icon-right v-if="item.isSign" class="mt-10px text-n-primary" />
                  <span v-else class="text-10px text-n-text-3 mt-8px">{{ config.texts.day(item.num) }}</span>
                </div>
              </div>
            </div>
            <!-- 第二行 3 天 -->
            <div class="grid grid-cols-4 space-x-8px mt-8px">
              <div
                v-for="(item, index) in progress.slice(4)"
                :key="item.num"
                class="text-center"
                :class="{ 'opacity-70': item.isSign }"
              >
                <div class="flex flex-col items-center pt-10px pb-6px bg-n-bg-tag b-rd-5px">
                  <img v-if="index + 4 !== progress.length - 1" class="w-24px h-24px" :src="config.resources.welfareGold">
                  <img v-else class="w-28px h-25px mb--1px" :src="config.resources.welfareBox">
                  <span class="mt-3px text-10px text-n-text-1">+{{ item.prizeNum }}</span>
                </div>
                <div class="flex justify-center">
                  <i-assets-icon-right v-if="item.isSign" class="mt-10px text-n-primary" />
                  <span v-else class="text-10px text-n-text-3 mt-8px">{{ config.texts.day(item.num) }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
        <div class="flex justify-center">
          <template v-if="isMoonpage">
            <div v-if="progress.length" class="w-full">
              <a
                class="rr-checkin-btn rr-moonpage-primary-btn block mt-4 w-full text-15px text-center rounded-full"
                :class="{
                  'rr-moonpage-primary-btn--disabled': signed,
                  'rr-moonpage-primary-btn--signed': signed,
                  'h-46px lh-46px': !signed,
                }"
                @click="handlePrimaryClick"
              >
                <span>{{ signed ? config.texts.moonpageCheckedInToday : config.texts.moonpageCheckIn(displayPrize(progressInfo)) }}</span>
                <span v-if="signed" class="rr-moonpage-tomorrow-copy">
                  {{ config.texts.moonpageComeBackTomorrow(tomorrowReward) }}
                </span>
              </a>
              <p v-if="signed" class="rr-moonpage-countdown mt-8px text-11px text-center text-n-text-3">
                {{ config.texts.moonpageNextCheckIn(nextCheckInCountdown) }}
              </p>
            </div>
          </template>
          <a
            v-else-if="progress.length"
            class="rr-checkin-btn block mt-4 w-200px h-9 lh-9 text-15px text-center btn-solid rounded-full"
            :class="{ 'opacity-70': signed }"
            @click="onCheckIn"
          >
            <span v-if="!signed">{{ config.texts.checkIn }}</span>
            <span v-else-if="checkInDayNum > 0 && checkInDayNum === progress.length">{{ config.texts.tomorrowToGetMore }}</span>
            <span v-else>{{ config.texts.tomorrowCheckIn }} {{ tomorrowInfo?.prizeNum }}</span>
          </a>
        </div>
      </div>

      <div v-if="tasks && tasks.length" class="relative z-2 mt-10px mx-10px px-10px pt-16px pb-0 bg-n-bg-card b-rd-2 rr-welfare-module">
        <h3 class="rr-section-title text-18px font-bold text-n-text-1">
          {{ config.texts.tasks }}
        </h3>

        <div
          v-for="(item, index) in tasks"
          :key="index"
          class="flex justify-between items-center py-16px px-12px border-b-solid border-b-1px border-n-border-primary last:border-b-0"
        >
          <div>
            <div class="text-n-text-1 font-medium">
              {{ item.taskTitle }}
            </div>
            <div class="text-n-primary text-12px flex items-center mt-4px">
              <img class="w-14px h-14px" :src="config.resources.welfareGold">
              <span class="ml-1" :style="{ color: config.theme.coinsColor }">{{ `+${item.coins}` }}</span>
            </div>
          </div>

          <div
            v-if="item.buttonState !== 1"
            class="rr-task-btn w-60px text-center py-4px b-rd-15px text-12px"
            :style="{ background: config.theme.checkinBtnGradient, color: '#FFFFFF' }"
            @click="handleTask(item)"
          >
            {{ getBtnStatus(item) }}
          </div>
          <div v-else class="w-60px flex items-center justify-center">
            <i-assets-icon-right class="color-n-primary" />
          </div>
        </div>
      </div>
    </div>

    <WelfareGuideAddScreen :value="showGuidePopupAddHome" @input="emit('update:show-guide-popup-add-home', $event)" />
    <WelfareGuideOpen :value="showGuideOpen" @input="emit('update:show-guide-open', $event)" />

    <BasicModal
      v-if="isMoonpage"
      :value="modalSigned"
      :closable="false"
      :mask-closable="true"
      class-name="rr-moonpage-checkin-modal"
      @input="emit('update:modal-signed', $event)"
    >
      <div class="rr-moonpage-modal-panel p-5">
        <div class="rr-moonpage-success-icon mx-auto flex items-center justify-center">
          <img class="rr-moonpage-success-coin" :src="config.resources.welfareGold" :alt="config.texts.moonpageCoinsEarned">
        </div>
        <div class="mt-12px text-center text-n-text-1">
          <p class="text-17px font-bold">
            {{ config.texts.moonpageCheckedIn }}
          </p>
          <p class="mt-2px text-26px lh-30px font-bold">
            <span class="rr-moonpage-modal-coins-value">+{{ displayPrize(progressInfo) }}</span>
            <span class="rr-moonpage-modal-coins-label text-17px"> {{ config.texts.coins }}</span>
          </p>
          <p class="mt-4px text-12px text-n-text-3">
            {{ config.texts.moonpageStreakDay(progressInfo?.num || checkInDayNum) }}
          </p>
        </div>
        <a
          class="rr-modal-ok-btn block mt-5 w-full h-10 lh-10 text-15px text-center btn-solid rounded-full"
          @click="onCheckInOk"
        >
          {{ config.texts.moonpageNiceContinue }}
        </a>
      </div>
    </BasicModal>
    <BasicModal v-else :value="modalSigned" @input="emit('update:modal-signed', $event)">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ config.texts.checkInGet }} <span class="text-n-primary">{{ progressInfo?.prizeNum }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="rr-modal-ok-btn block mt-4 w-full h-9 lh-9 text-15px text-center btn-solid rounded-full"
          @click="onCheckInOk"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <BasicModal
      v-if="isMoonpage"
      :value="signedNoticePopup"
      :closable="false"
      :mask-closable="true"
      class-name="rr-moonpage-signed-modal"
      @input="emit('update:signed-notice-popup', $event)"
    >
      <div class="rr-moonpage-modal-panel relative p-5 text-center">
        <button
          class="rr-moonpage-close-btn absolute top-4 right-4 flex items-center justify-center"
          type="button"
          :aria-label="config.texts.moonpageClose"
          @click="emit('update:signed-notice-popup', false)"
        >
          <span class="rr-moonpage-close-ring" aria-hidden="true">
            <span class="rr-moonpage-close-mark" />
          </span>
        </button>
        <div class="rr-moonpage-signed-icon mx-auto flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m7.5 12.25 3 3 6-6" />
          </svg>
        </div>
        <p class="mt-12px text-15px lh-20px font-bold text-n-text-1">
          {{ config.texts.moonpageSignedNoticeTitle }}
        </p>
        <p class="mt-6px text-12px lh-18px text-n-text-3">
          {{ config.texts.moonpageSignedNoticeText }}
        </p>
        <div class="rr-moonpage-browse-panel mt-4 p-3">
          <p class="text-12px text-n-text-2">
            {{ config.texts.moonpageWhileWait }}
          </p>
          <a class="rr-moonpage-browse-btn block mt-2 h-9 lh-9 text-13px text-center rounded-full" @click="onBrowseGenres">
            {{ config.texts.moonpageBrowseGenres }}
          </a>
        </div>
        <a
          class="rr-moonpage-got-it-btn block mt-3 h-9 lh-9 text-13px text-center rounded-full"
          @click="emit('update:signed-notice-popup', false)"
        >
          {{ config.texts.moonpageGotIt }}
        </a>
      </div>
    </BasicModal>

    <BasicModal :value="finishPopup" @input="emit('update:finish-popup', $event)">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ completeTaskDescription }} {{ config.texts.get }} <span class="text-n-primary">{{ coins }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="rr-modal-ok-btn block mt-4 w-full h-9 lh-9 text-15px text-center btn-solid rounded-full"
          @click="emit('update:finish-popup', false)"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>
  </div>
</template>

<style scoped>
.rr-section-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.cover {
  background-repeat: no-repeat;
  background-position: top left;
  background-size: 100% auto;
}

.rr-moonpage-wallet-row {
  min-height: 52px;
}

.rr-moonpage-day-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.rr-moonpage-day-card {
  position: relative;
  min-height: 92px;
  padding: 16px 6px 10px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: #f3f5fa;
  color: #1f2b4d;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.rr-moonpage-day-card--done {
  background: #d9dee8;
  border-color: #c7cedb;
  color: #7b8497;
}

.rr-moonpage-day-card--done .rr-moonpage-prize {
  color: #737d92;
}

.rr-moonpage-day-card--today {
  border: 2px solid #4c7dff;
  box-shadow: 0 0 0 2px rgba(76, 125, 255, 0.12);
}

.rr-moonpage-day-card--bonus {
  grid-column: span 2;
  border: 0;
  background: linear-gradient(135deg, #a37cf5 0%, #e7489a 100%);
  color: #fff;
}

.rr-moonpage-day-card--done.rr-moonpage-day-card--bonus {
  background: #d9dee8;
  border-color: #c7cedb;
  color: #7b8497;
}

/* The Day 7 bonus card gets the same selection outline as the other Today cards. */
.rr-moonpage-day-card--bonus.rr-moonpage-day-card--today {
  border: 2px solid #4c7dff;
  box-shadow: 0 0 0 2px rgba(76, 125, 255, 0.12);
}

.rr-moonpage-today-tag {
  position: absolute;
  top: -9px;
  left: 50%;
  transform: translateX(-50%);
  padding: 2px 8px;
  border-radius: 999px;
  background: #4c7dff;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  white-space: nowrap;
}

.rr-moonpage-coin,
.rr-moonpage-gift {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.rr-moonpage-gift {
  width: 28px;
  height: 25px;
}

.rr-moonpage-check-badge {
  width: 24px;
  height: 24px;
  color: #4c7dff;
}

.rr-moonpage-check-badge svg,
.rr-moonpage-signed-icon svg {
  display: block;
  width: 100%;
  height: 100%;
}

.rr-moonpage-check-badge circle {
  fill: currentColor;
  opacity: 0.16;
  stroke: currentColor;
  stroke-width: 1.5;
}

.rr-moonpage-check-badge path {
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.rr-moonpage-prize {
  margin-top: 5px;
  font-size: 13px;
  line-height: 18px;
}

.rr-moonpage-day-label {
  margin-top: 3px;
  font-size: 10px;
  line-height: 14px;
  opacity: 0.8;
}

.rr-moonpage-day-card--bonus .rr-moonpage-day-label {
  opacity: 1;
  font-weight: 700;
}

.rr-rules-toggle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #edf0f8;
  color: #64748b;
}

.rr-rules-panel {
  padding: 10px 12px;
  border-radius: 10px;
  background: #eef1fb;
  line-height: 18px;
}

.rr-rules-panel p + p {
  margin-top: 2px;
}

.rr-moonpage-primary-btn {
  background: v-bind(moonpageActionGradient);
  color: #fff !important;
  font-weight: 700;
}

.rr-moonpage-primary-btn--disabled {
  background: #cbd2df;
  color: #fff !important;
  cursor: pointer;
}

.rr-moonpage-primary-btn--signed {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 46px;
  min-height: 46px;
  padding: 5px 16px;
  line-height: 20px;
}

.rr-moonpage-tomorrow-copy {
  margin-top: 2px;
  font-size: 11px;
  line-height: 14px;
  font-weight: 700;
}

.rr-moonpage-countdown {
  letter-spacing: 0.01em;
}

.rr-moonpage-success-icon {
  width: 64px;
  height: 64px;
  padding: 14px;
  border-radius: 50%;
  background: #fff0c2;
  color: #f6a91a;
}

.rr-moonpage-success-coin {
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.rr-moonpage-modal-coins-value {
  color: #f4980f;
}

.rr-moonpage-modal-coins-label {
  color: #1b1d3a;
}

.rr-moonpage-signed-icon {
  width: 52px;
  height: 52px;
  padding: 13px;
  border-radius: 50%;
  background: #e2f8ef;
  color: #2dbb88;
}

.rr-moonpage-signed-icon path {
  stroke: currentColor;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.rr-moonpage-browse-panel {
  border-radius: 12px;
  background: #eef1fb;
}

.rr-moonpage-browse-btn {
  background: linear-gradient(90deg, #5b64f6 0%, #7481f8 100%);
  color: #fff !important;
  font-weight: 700;
}

.rr-moonpage-modal-panel {
  position: relative;
}

.rr-moonpage-close-btn {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 50%;
  background: #eef1fb;
}

.rr-moonpage-close-ring {
  position: relative;
  width: 18px;
  height: 18px;
  border-radius: 50%;
}

.rr-moonpage-close-mark::before,
.rr-moonpage-close-mark::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 2px;
  border-radius: 999px;
  background: #1f2b4d;
}

.rr-moonpage-close-mark::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.rr-moonpage-close-mark::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.rr-moonpage-got-it-btn {
  border: 1px solid #e5e9f2;
  color: #1f2b4d !important;
  font-weight: 700;
}

.rr-checkin-btn {
  color: #FFFFFF !important;
}

.rr-modal-ok-btn {
  color: #FFFFFF !important;
}
</style>
